import React, { useState, useRef, useEffect, useContext } from 'react';
import Logo from './Logo';
import api from '../api';
import { AuthContext } from '../context/AuthContext';

export default function Chatbot(){
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { who: 'bot', text: 'Hi — I can help you find jobs, apply, or learn about screening steps.' }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const { user } = useContext(AuthContext);
  const [conversationId, setConversationId] = useState(null);

  useEffect(() => { if(listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, open]);

  async function send(){
    if(!input.trim() || loading) return;
    const text = input.trim();
    setMessages(m => [...m, { who: 'user', text }]);
    setInput('');
    setError('');
    setLoading(true);

    try{
      if(!user){
        throw new Error('Please log in to use the assistant and persist conversations.');
      }
      // Build a compact history for server
      const history = messages.map(m => ({ role: m.who === 'bot' ? 'assistant' : 'user', content: m.text }));
      const res = await api.post('/api/chat', { message: text, history, conversationId });
      const data = res.data;
      const reply = data.reply || 'Sorry — I had no response.';
      setMessages(m => [...m, { who: 'bot', text: reply }]);
      if(data.conversationId) setConversationId(data.conversationId);
    }catch(err){
      console.error('Chat error', err);
      setError(err.response?.data?.error || err.message || 'Chat failed');
      setMessages(m => [...m, { who: 'bot', text: 'Sorry — the chat service is unavailable right now.' }]);
    }finally{
      setLoading(false);
    }
  }

  // Load conversation history when opening and when user changes
  useEffect(()=>{
    let mounted = true;
    async function load(){
      if(!open) return;
      if(!user) return;
      try{
        const res = await api.get('/api/chat/history');
        if(!mounted) return;
        const { messages: msgs, conversationId: cid } = res.data || {};
        if(Array.isArray(msgs) && msgs.length) setMessages(msgs);
        if(cid) setConversationId(cid);
      }catch(err){
        console.warn('Could not load chat history', err);
      }
    }
    load();
    return ()=>{ mounted = false; };
  }, [open, user]);

  return (
    <>
      <div
        title="Chat with Talent Link"
        className="chatbot-button"
        style={{ background: 'linear-gradient(180deg,var(--color-primary-medium),var(--color-primary-deep))' }}
        onClick={() => setOpen(v => !v)}
        aria-label="Open chat"
      >
        <Logo size={40} />
      </div>

      {open && (
        <div className="chatbot-window">
          <div className="p-3 bg-primary-deep text-white flex items-center gap-3">
            <Logo size={32} />
            <div>
              <div className="font-semibold">Talent Link Assistant</div>
              <div className="text-xs opacity-80">Quick help for jobs & applications</div>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto text-white opacity-80">✕</button>
          </div>

          <div ref={listRef} className="chatbot-messages">
            {messages.map((m, idx) => (
              <div key={idx} className={`chatbot-msg ${m.who === 'user' ? 'user' : 'bot'}`}>{m.text}</div>
            ))}
          </div>

          {error && <div className="p-2 text-sm text-red-600">{error}</div>}

          <div className="chatbot-input">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if(e.key === 'Enter') send(); }}
              className="flex-1 px-3 py-2 rounded border border-secondary-verylight outline-none"
              placeholder="Ask me about jobs, applications or screening..."
              disabled={loading}
            />
            <button onClick={send} className="bg-primary-medium text-white px-4 py-2 rounded" disabled={loading}>{loading ? '...' : 'Send'}</button>
          </div>
        </div>
      )}
    </>
  );
}
